import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { catchError, of, filter, switchMap, take } from 'rxjs';

import { AjaxService } from '../../../services/ajax.service';
import { ViewScopeService, Customer } from '../../../services/view-scope.service';
import { TranslateService } from '../../../services/translate.service';

import { LAST_TIME_DONE_SOME } from '../../../services/account-api.constants';

interface LastTimeDoneSomeRow {
    ID: number;
    LastDate: string | null;
    Descr: string;
    Descr_ID: string | null;
    ByUser: string | null;
    Status: string | null;
}

@Component({
    standalone: true,
    selector: 'app-doc-duration-widget',
    imports: [CommonModule, ProgressBarModule, TableModule],
    templateUrl: './docdurationwidget.html',
    styleUrl: './docdurationwidget.scss'
})
export class DocumentDurationWidget implements OnInit {
    lastTimeDoneItems: LastTimeDoneSomeRow[] = [];
    loading = false;
    error: string | null = null;

    constructor(
        private ajaxService: AjaxService,
        public  viewScopeService: ViewScopeService,
        public  translateServ: TranslateService, 
    ) { }

    ngOnInit(): void {
        this.loadLastTimeDoneSome();
    }

    loadLastTimeDoneSome(): void {
        this.loading = true;
        this.error = null;

        this.getLastTimeDoneSome().pipe(
            catchError(error => {
                console.error('DocumentDurationWidget - API error', error);
                this.error = 'Failed to load latest activity data';
                this.loading = false;
                return of({ Table: [] });
            })
        ).subscribe((response: any) => {
            console.log('DocumentDurationWidget - LAST_TIME_DONE_SOME response', response);
            this.loading = false;
            this.lastTimeDoneItems = this.getLastTimeDoneSomeRows(response);
        });
    }

    // Waits for ViewScopeService's selectedCustomer$ (BehaviorSubject, always replays its latest
    // value) instead of reading it synchronously - avoids racing SelectAccountComponent, which
    // populates the selected customer asynchronously on the same tick this widget mounts.
    getLastTimeDoneSome() {
        const userId = this.viewScopeService.getCurrentOpertaingUserId();

        return this.viewScopeService.selectedCustomer$.pipe(
            filter((customer): customer is Customer => customer != null),
            take(1),
            switchMap(customer => {
                const apiUrl = this.ajaxService.getBaseOfApi() + LAST_TIME_DONE_SOME + customer.id + '/' + userId;
                return this.ajaxService.getData(apiUrl);
            })
        );
    }

    private getLastTimeDoneSomeRows(response: any): LastTimeDoneSomeRow[] {
        const parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;
        return Array.isArray(parsedResponse?.Table) ? parsedResponse.Table : [];
    }

    getStatusSeverity(status: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (!status) return 'secondary';

        const normalizedStatus = status.toLowerCase();

        if (normalizedStatus === 'completed') return 'success';
        if (normalizedStatus === 'failed' || normalizedStatus === 'error') return 'danger';
        if (normalizedStatus === 'pending') return 'warn';

        return 'info';
    }

    getStatusLabel(status: string | null): string {
        return status || 'No status';
    }
}